import type { MaybeRefOrGetter } from 'vue';
import { toValue, watch } from 'vue';

import type { UserPreferredLanguage } from './usePreferredLanguage';
import { usePreferredLanguage } from './usePreferredLanguage';

type AuthenticatedUser = UserPreferredLanguage & { id: string };

/**
 * Applies {@link syncPreferredLanguageOnLogin} whenever the authenticated user becomes available
 * or is refreshed (e.g. after `Auth/getMe` commits `Auth/me`).
 */
export function usePreferredLanguageLoginWatcher(
  authenticatedUser: MaybeRefOrGetter<AuthenticatedUser | null | undefined>,
  options?: {
    onApplied?: () => void;
  }
) {
  const { syncPreferredLanguageOnLogin } = usePreferredLanguage();

  async function syncFromAuthenticatedUser(user: AuthenticatedUser | null | undefined) {
    if (!user?.id) {
      return;
    }

    await syncPreferredLanguageOnLogin(user);
    options?.onApplied?.();
  }

  watch(
    () => toValue(authenticatedUser)?.id,
    () => {
      syncFromAuthenticatedUser(toValue(authenticatedUser));
    },
    { immediate: true }
  );
}
